/**
 * "Text me flight alerts": an optional, unchecked box under the checkout
 * acknowledgments (founder, 2026-10-24). Ticking it turns on flight alert
 * texts for the account, to the lead traveler's phone, recording the consent
 * with the words shown here (src/constants/flightAlertTexts.js); unticking
 * turns them off. Nothing here gates the purchase.
 *
 * Renders nothing until texts are switched on (FLIGHT_STATUS_TEXTS), or when
 * the lead traveler's phone isn't a number we can text.
 */
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { trackEvent } from '@/lib/activityTracker';
import { textAlertsApi } from '@/lib/textAlertsApi';
import { FLIGHT_ALERT_TEXTS_DISCLOSURE, FLIGHT_ALERT_TEXTS_LABEL, textablePhone } from '@/constants/flightAlertTexts';
import { renderDocLink } from './checkoutAcknowledgmentsContent';

export default function CheckoutTextAlertsOptIn({ phone, onOpenDocument, className }) {
  const [state, setState] = useState(null);
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    textAlertsApi.get().then((s) => {
      if (!alive) return;
      setState(s);
      // Already on for the account: show it on, never pre-tick a new consent.
      setChecked(Boolean(s?.enabled));
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  if (!state?.available || !textablePhone(phone)) return null;

  const onChange = async (next) => {
    setError(null);
    setChecked(next);
    setBusy(true);
    try {
      setState(next ? await textAlertsApi.turnOn({ phone, source: 'checkout' }) : await textAlertsApi.turnOff());
      trackEvent('checkout_text_alerts', { on: next });
    } catch (e) {
      setChecked(!next);
      setError(e.message || 'Something went wrong. You can turn texts on later from your trip.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={cn('mb-8 -mt-4 text-left max-w-2xl mx-auto', className)}>
      <label className="flex items-start gap-3 cursor-pointer">
        <Checkbox
          id="review-text-alerts"
          checked={checked}
          disabled={busy}
          onCheckedChange={(v) => onChange(!!v)}
          className="border-white/30 data-[state=checked]:bg-brand-600 data-[state=checked]:border-brand-600 mt-0.5 flex-shrink-0 h-5 w-5"
        />
        <span className="text-white/75 text-[13px] font-normal leading-snug">
          {FLIGHT_ALERT_TEXTS_LABEL} at {phone} <span className="text-white/45">(optional)</span>
          <span className="block mt-1 text-white/45 text-[12px]" data-testid="text-alerts-disclosure">
            {FLIGHT_ALERT_TEXTS_DISCLOSURE} See our {renderDocLink('terms', onOpenDocument, 'Terms')} and{' '}
            {renderDocLink('privacy', onOpenDocument, 'Privacy Policy')}.
          </span>
        </span>
      </label>
      {error && <p className="mt-2 ml-8 text-[12px] font-normal text-brand-300">{error}</p>}
    </div>
  );
}

CheckoutTextAlertsOptIn.propTypes = {
  phone: PropTypes.string,
  onOpenDocument: PropTypes.func.isRequired,
  className: PropTypes.string,
};
